/**
 * Dev Session Tracking Service
 *
 * Records admin/superadmin activity sessions as development cost entries.
 * Tracks active time (keyboard + mouse), AI interactions, and stores
 * compressed event counts — NOT actual keystrokes — in Firestore.
 *
 * Cost model: active_hours × HOURLY_RATE + AI_tokens × TOKEN_COST
 */

import { summariseDevSessions, type DevGlance } from '@/lib/dev-glance';
import { devCostSek } from '@/lib/dev-rate';
import { db } from '@/lib/firebase';
import {
  collection, addDoc, getDocs, query, where, orderBy, limit,
  Timestamp, updateDoc, doc, getDoc, setDoc, serverTimestamp,
} from 'firebase/firestore';

/** Legacy: what new sessions still store in `costEur`. Displays use the SCB
 *  salary rate in lib/dev-rate.ts (Gordon 2026-11-02), recomputed from time. */
export const DEV_HOURLY_RATE_EUR = 150;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DevSession {
  id: string;
  userId: string;
  userName: string;
  startTime: Timestamp;
  endTime?: Timestamp;
  activeDurationSeconds: number; // excludes idle periods >5 min
  type: 'ai' | 'manual' | 'review';
  description?: string;
  /** Compressed activity: array of [minute_offset, keystrokes, mouse_events] */
  activityBuckets?: [number, number, number][];
  keystrokesTotal: number;
  mouseEventsTotal: number;
  costEur: number; // activeDurationSeconds / 3600 × DEV_HOURLY_RATE_EUR
  aiTokensUsed?: number;
  aiCostEur?: number;
}

export interface DevSessionSummary {
  totalSessions: number;
  totalActiveHours: number;
  /** Kronor at the SCB salary rate (lib/dev-rate.ts). */
  totalCostSek: number;
  totalKeystrokes: number;
  totalMouseEvents: number;
  /** Chronological; `cost` in kronor. */
  byMonth: { month: string; hours: number; cost: number }[];
  recent: DevSession[];
  /** Everything the Dev Sessions cards and their panels show. */
  glance: DevGlance;
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

const COL = 'devSessions';

export async function startDevSession(
  userId: string,
  userName: string,
  type: DevSession['type'] = 'manual',
  description?: string,
): Promise<string> {
  const docRef = await addDoc(collection(db, COL), {
    userId,
    userName,
    startTime: serverTimestamp(),
    activeDurationSeconds: 0,
    type,
    description: description || '',
    activityBuckets: [],
    keystrokesTotal: 0,
    mouseEventsTotal: 0,
    costEur: 0,
  });
  return docRef.id;
}

export async function updateDevSession(
  sessionId: string,
  data: {
    activeDurationSeconds: number;
    keystrokesTotal: number;
    mouseEventsTotal: number;
    activityBuckets: [number, number, number][];
    costEur: number;
    endTime?: Timestamp;
  },
): Promise<void> {
  await updateDoc(doc(db, COL, sessionId), {
    ...data,
    endTime: data.endTime || serverTimestamp(),
  });
}

export async function closeDevSession(
  sessionId: string,
  activeDurationSeconds: number,
  keystrokesTotal: number,
  mouseEventsTotal: number,
  activityBuckets: [number, number, number][],
): Promise<void> {
  const costEur = (activeDurationSeconds / 3600) * DEV_HOURLY_RATE_EUR;
  await updateDoc(doc(db, COL, sessionId), {
    endTime: serverTimestamp(),
    activeDurationSeconds,
    keystrokesTotal,
    mouseEventsTotal,
    activityBuckets,
    costEur: Math.round(costEur * 100) / 100,
  });
}

/** Log a discrete AI update session (called externally, e.g. from admin panel) */
export async function logAiUpdateSession(
  userId: string,
  userName: string,
  description: string,
  durationMinutes: number,
  aiTokensUsed?: number,
): Promise<string> {
  const activeDurationSeconds = durationMinutes * 60;
  const costEur = (activeDurationSeconds / 3600) * DEV_HOURLY_RATE_EUR;
  const aiCostEur = aiTokensUsed ? (aiTokensUsed / 1_000_000) * 15 : 0; // ~€15/M tokens
  const docRef = await addDoc(collection(db, COL), {
    userId,
    userName,
    startTime: serverTimestamp(),
    endTime: serverTimestamp(),
    activeDurationSeconds,
    type: 'ai',
    description,
    activityBuckets: [],
    keystrokesTotal: 0,
    mouseEventsTotal: 0,
    costEur: Math.round(costEur * 100) / 100,
    aiTokensUsed: aiTokensUsed || 0,
    aiCostEur: Math.round(aiCostEur * 100) / 100,
  });
  return docRef.id;
}

export async function getDevSessions(limitCount = 50): Promise<DevSession[]> {
  try {
    const q = query(collection(db, COL), orderBy('startTime', 'desc'), limit(limitCount));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }) as DevSession);
  } catch {
    // No index yet — fallback without orderBy
    const snap = await getDocs(collection(db, COL));
    return snap.docs
      .map(d => ({ id: d.id, ...d.data() }) as DevSession)
      .sort((a, b) => (b.startTime?.seconds || 0) - (a.startTime?.seconds || 0))
      .slice(0, limitCount);
  }
}

export async function getDevSessionSummary(): Promise<DevSessionSummary> {
  // Every session — the old 200 cap made the totals stop at 200 sessions.
  const sessions = await getDevSessions(5000);
  const glance = summariseDevSessions(sessions.map(s => ({
    startMs: s.startTime?.toMillis?.() ?? 0,
    seconds: s.activeDurationSeconds || 0,
    type: s.type || 'manual',
    person: s.userName || '',
    costEur: devCostSek(s.activeDurationSeconds || 0), // kronor, at the SCB rate
    keystrokes: s.keystrokesTotal || 0,
    mouse: s.mouseEventsTotal || 0,
    description: s.description,
    aiTokens: s.aiTokensUsed,
    aiCostEur: s.aiCostEur,
  })));
  return {
    totalSessions: glance.sessions,
    totalActiveHours: Math.round(glance.hours * 10) / 10,
    totalCostSek: Math.round(glance.costEur),
    totalKeystrokes: glance.keystrokes,
    totalMouseEvents: glance.mouse,
    byMonth: glance.byMonth.slice(-12).map(m => ({
      month: new Date(`${m.key}-01T00:00:00Z`).toLocaleString('en', { year: 'numeric', month: 'short', timeZone: 'UTC' }),
      hours: Math.round(m.hours * 10) / 10,
      cost: Math.round(m.costEur),
    })),
    recent: sessions.slice(0, 10),
    glance,
  };
}

// ─── Activity tracker hook helper ─────────────────────────────────────────────
// Meant to be used in a React hook — this is pure logic, no React imports.

export interface ActivityTracker {
  sessionId: string;
  startMs: number;
  lastActiveMs: number;
  activeDurationMs: number;
  keystrokesTotal: number;
  mouseEventsTotal: number;
  buckets: [number, number, number][]; // [minute_offset, keys, mouse]
  currentBucketMinute: number;
  currentBucketKeys: number;
  currentBucketMouse: number;
}

export function createActivityTracker(sessionId: string): ActivityTracker {
  return {
    sessionId,
    startMs: Date.now(),
    lastActiveMs: Date.now(),
    activeDurationMs: 0,
    keystrokesTotal: 0,
    mouseEventsTotal: 0,
    buckets: [],
    currentBucketMinute: 0,
    currentBucketKeys: 0,
    currentBucketMouse: 0,
  };
}

const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes idle = not counted

export function recordKeystroke(tracker: ActivityTracker): ActivityTracker {
  const now = Date.now();
  const minuteOffset = Math.floor((now - tracker.startMs) / 60_000);
  const wasIdle = now - tracker.lastActiveMs > IDLE_THRESHOLD_MS;
  const activeAdd = wasIdle ? 0 : now - tracker.lastActiveMs;

  let buckets = tracker.buckets;
  let curMin = tracker.currentBucketMinute;
  let curKeys = tracker.currentBucketKeys;
  let curMouse = tracker.currentBucketMouse;

  if (minuteOffset !== curMin) {
    // Flush current bucket
    buckets = [...buckets, [curMin, curKeys, curMouse]];
    curMin = minuteOffset;
    curKeys = 0;
    curMouse = 0;
  }

  return {
    ...tracker,
    lastActiveMs: now,
    activeDurationMs: tracker.activeDurationMs + activeAdd,
    keystrokesTotal: tracker.keystrokesTotal + 1,
    currentBucketMinute: curMin,
    currentBucketKeys: curKeys + 1,
    currentBucketMouse: curMouse,
    buckets,
  };
}

export function recordMouseEvent(tracker: ActivityTracker): ActivityTracker {
  const now = Date.now();
  const minuteOffset = Math.floor((now - tracker.startMs) / 60_000);
  const wasIdle = now - tracker.lastActiveMs > IDLE_THRESHOLD_MS;
  const activeAdd = wasIdle ? 0 : now - tracker.lastActiveMs;

  let buckets = tracker.buckets;
  let curMin = tracker.currentBucketMinute;
  let curKeys = tracker.currentBucketKeys;
  let curMouse = tracker.currentBucketMouse;

  if (minuteOffset !== curMin) {
    buckets = [...buckets, [curMin, curKeys, curMouse]];
    curMin = minuteOffset;
    curKeys = 0;
    curMouse = 0;
  }

  return {
    ...tracker,
    lastActiveMs: now,
    activeDurationMs: tracker.activeDurationMs + activeAdd,
    mouseEventsTotal: tracker.mouseEventsTotal + 1,
    currentBucketMinute: curMin,
    currentBucketKeys: curKeys,
    currentBucketMouse: curMouse + 1,
    buckets,
  };
}
